import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Clock, DollarSign, BookPlus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookCard from "@/components/BookCard";
import MoodChip from "@/components/MoodChip";
import { moods } from "@/data/books";
import { useBooks } from "@/hooks/useBooks";
import { useAuth } from "@/hooks/useAuth";
import ForYouFeed from "@/components/ForYouFeed";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" as const },
  }),
};

const Index = () => {
  const { books } = useBooks();
  const { user, loading, isAdmin, isAuthor } = useAuth();
  const featuredBooks = books.filter((b) => b.featured).slice(0, 4);

  // Logged-in readers get the personalized For You feed; admins/authors keep the marketing home.
  if (!loading && user && !isAdmin && !isAuthor) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <ForYouFeed />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Romance Book Recommendations Quiz | Romance Radar</title>
        <meta name="description" content="Find your next romance read in 60 seconds. Take the quiz — match by tropes, spice, and emotional intensity." />
        <link rel="canonical" href="https://romanceradars.live/" />
        <meta property="og:title" content="Romance Book Recommendations Quiz | Romance Radar" />
        <meta property="og:description" content="Find your next romance read in 60 seconds. Take the quiz — match by tropes, spice, and emotional intensity." />
        <meta property="og:url" content="https://romanceradars.live/" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-gold/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: "1.5s" }} />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-primary text-sm font-semibold tracking-widest uppercase mb-4"
          >
            For readers who feel everything
          </motion.p>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-tight mb-6 max-w-4xl mx-auto">
            Don't know what to read next?{" "}
            <span className="text-gradient">We'll tell you.</span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-8"
          >
            Find your next emotionally addictive romance in seconds. Curated by mood, tropes, and spice level. Written by indie authors only.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-3"
          >
            <Link
              to="/discover"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-lg font-semibold hover:opacity-90 transition-opacity glow-blush"
            >
              Find My Next Read <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/submit-book"
              className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground border border-border px-8 py-4 rounded-full text-lg font-semibold hover:bg-secondary/80 transition-colors"
            >
              <BookPlus className="w-5 h-5" /> Submit Your Book
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Mood Picker */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
            <motion.h2 variants={fadeUp} custom={0} className="font-display text-3xl md:text-4xl font-bold mb-3">
              Pick Your <span className="text-gradient">Mood</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-muted-foreground">
              What are you craving right now?
            </motion.p>
          </motion.div>
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto"
          >
            {moods.map((mood, i) => (
              <motion.div key={mood.id} variants={fadeUp} custom={i + 2}>
                <Link to={`/discover?mood=${mood.id}`}>
                  <MoodChip label={mood.label} />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Books */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
            <motion.h2 variants={fadeUp} custom={0} className="font-display text-3xl md:text-4xl font-bold mb-3">
              Books That Will <span className="text-gradient">Wreck You</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-muted-foreground">
              Handpicked for maximum emotional damage.
            </motion.p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {featuredBooks.map((book, i) => (
              <motion.div
                key={book.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <BookCard book={book} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="font-display text-3xl md:text-4xl font-bold text-center mb-12"
          >
            Why <span className="text-gradient">This Is Different</span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { icon: Clock, title: "No Wasted Time", desc: "Stop scrolling through thousands of books. We match you in seconds based on exactly what you're craving." },
              { icon: Sparkles, title: "Curated, Not Random", desc: "Every book is handpicked for emotional impact. No filler, no duds—just stories that hit different." },
              { icon: DollarSign, title: "Affordable Reading", desc: "Kindle Unlimited friendly, indie author focused. Great reads without breaking the bank." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="glass rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
